import { useRef, useState } from 'react';

/**
 * BottomSheet 드래그-닫기 hook.
 * - 핸들 영역 (data-sheet-handle="true")에서 시작: 즉시 드래그 활성, 짧은 탭은 닫기
 * - 본문 영역에서 시작: scrollTop=0 + 5px 이상 아래로 끌어야 활성
 * - input/textarea/select 시작: 드래그 무시 (키보드 정상 동작)
 * - distance > 100px: onClose
 *
 * @param {() => void} onClose
 * @param {{ current?: HTMLElement | null }} [scrollRef] - 본문 스크롤 요소 (optional, 없으면 본문 스크롤 0 가정)
 */
export default function useSheetDrag(onClose, scrollRef) {
    const [dragY, setDragY] = useState(0);
    const [dragging, setDragging] = useState(false);
    const dragStartY = useRef(null);
    const dragActivated = useRef(false);
    const fromHandle = useRef(false);
    const startScrollTop = useRef(0);

    const reset = () => {
        setDragY(0);
        setDragging(false);
        dragStartY.current = null;
        dragActivated.current = false;
        fromHandle.current = false;
    };

    const handleSheetTouchStart = (e) => {
        const target = e.target;
        const tag = target.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select'
            || target.closest?.('button, a, label, [role="button"], [role="slider"], [role="switch"], [data-tappable="true"]')) {
            dragStartY.current = null;
            return;
        }
        const isHandle = !!target.closest?.('[data-sheet-handle="true"]');
        dragStartY.current = e.touches[0].clientY;
        startScrollTop.current = scrollRef?.current?.scrollTop || 0;
        fromHandle.current = isHandle;
        dragActivated.current = isHandle;
        if (isHandle) setDragging(true);
    };

    const handleSheetTouchMove = (e) => {
        if (dragStartY.current == null) return;
        const dy = e.touches[0].clientY - dragStartY.current;
        if (!dragActivated.current) {
            if (dy < -3 || startScrollTop.current > 0) {
                dragStartY.current = null;
                return;
            }
            if (dy > 5) {
                dragActivated.current = true;
                setDragging(true);
                dragStartY.current = e.touches[0].clientY;
                setDragY(0);
            }
            return;
        }
        const cur = e.touches[0].clientY - dragStartY.current;
        setDragY(Math.max(0, cur));
    };

    const handleSheetTouchEnd = () => {
        if (dragStartY.current == null && !dragActivated.current) return;
        const wasDragging = dragActivated.current;
        const wasHandle = fromHandle.current;
        const distance = dragY;
        reset();
        if (!wasDragging) return;
        if (wasHandle && distance < 5) { onClose(); return; }
        if (distance > 100) { onClose(); }
    };

    const handleHandleMouseDown = (clientY) => {
        dragStartY.current = clientY;
        dragActivated.current = true;
        fromHandle.current = true;
        setDragging(true);
    };
    const handleHandleMouseMove = (clientY) => {
        if (dragStartY.current == null) return;
        const dy = clientY - dragStartY.current;
        setDragY(Math.max(0, dy));
    };
    const handleHandleMouseEnd = () => {
        if (dragStartY.current == null) return;
        const distance = dragY;
        reset();
        if (distance < 5 || distance > 100) { onClose(); }
    };

    return {
        dragY,
        dragging,
        sheetHandlers: {
            onTouchStart: handleSheetTouchStart,
            onTouchMove: handleSheetTouchMove,
            onTouchEnd: handleSheetTouchEnd,
            onTouchCancel: reset,
        },
        handleMouseHandlers: {
            onMouseDown: (e) => handleHandleMouseDown(e.clientY),
            onMouseMove: (e) => { if (dragging) handleHandleMouseMove(e.clientY); },
            onMouseUp: handleHandleMouseEnd,
            onMouseLeave: () => { if (dragging) handleHandleMouseEnd(); },
        },
    };
}
